import React, { useMemo } from 'react';
import { Skeleton, Typography } from '@revfluence/fresh';
import { BoxCheckIcon } from '@revfluence/fresh-icons/regular/esm';
import { useGetCatalogs } from '@frontend/app/containers/Settings/ProductFulfillment/Catalogs/hooks/useGetCatalogs';
import { useGetProjectConfigByType } from '@frontend/applications/ProductFulfillmentApp/hooks/useGetProjectConfigByType';
import { ProjectConfigType } from '@frontend/applications/ProductFulfillmentApp/types/globalTypes';
import { useGetAllCatalogStats } from '@frontend/app/containers/Settings/ProductFulfillment/Catalogs/hooks/useGetAllCatalogStats';
import { PlusIcon } from '@revfluence/fresh-icons/solid/esm';
import { Button as ShadcnButton } from '@frontend/shadcn/components/ui/button';
import { SectionCard } from '@frontend/app/containers/Projects/CampaignPage/components/SectionCard';
import { useProjectByIdQuery } from '@frontend/app/hooks';
import { useAppContext } from '@frontend/context/AppContext';
import { ApolloProvider } from '@apollo/client';
import { SidebarLayout } from '../components/SidebarLayout';
import styles from './ProductCatalogs.scss';
import { useProductCatalogsContext } from './ProductCatalogsContext';
import { ConnectCatalogDrawer } from './ConnectCatalogDrawer';
import { ProductCatalogsProps } from './ProductCatalogs';
import { CatalogsListV2 } from './CatalogsListV2';
import ProductEmailTemplate from './ProductEmailTemplate';

const { Text, Title } = Typography;

const EmptyScreen = () => {
  const { setIsDrawerOpen } = useProductCatalogsContext();

  return (
    <div className={styles.emptyScreen}>
      <div
        style={{
          display: 'flex',
          backgroundColor: '#F9FAFB',
          justifyContent: 'space-between',
          alignItems: 'center',
          width: '100%',
          padding: '24px'
        }}
      >
        <div>
          <Title level={4}>Connect Product Catalogs</Title>
          <Text style={{ display: 'block', marginTop: '8px', marginBottom: '4px' }}>
            Connect your Shopify Product Catalogs and
          </Text>
          <Text style={{ display: 'block', marginBottom: '16px' }}>
            enable creators to request products.
          </Text>
          <ShadcnButton onClick={() => setIsDrawerOpen(true)} className="flex items-center gap-2">
            <PlusIcon />
            Connect Catalogs
          </ShadcnButton>
        </div>
        <div>
          <BoxCheckIcon style={{ fontSize: '150px', color: '#f0f0f0' }} />
        </div>
      </div>
    </div>
  );
};

export const ProductCatalogContent = ({ programId }: ProductCatalogsProps) => {
  const { catalogs, loading } = useGetCatalogs();
  const { allCatalogStats } = useGetAllCatalogStats();
  const { projectConfig } = useGetProjectConfigByType({
    variables: {
      projectId: programId,
      type: ProjectConfigType.ProductCatalog,
    },
    skip: !programId,
  });
  const { setIsDrawerOpen, setSelectedCatalogIds } = useProductCatalogsContext();

  const selectedCatalogs = useMemo(
    () => catalogs.filter((catalog) => projectConfig?.brandCatalogs?.includes(catalog.id)),
    [catalogs, projectConfig?.brandCatalogs],
  );

  const handleOpenDrawer = () => {
    setIsDrawerOpen(true);
    setSelectedCatalogIds(projectConfig?.brandCatalogs?.map((id) => String(id)) || []);
  };

  const connectCatalogButton = (
    <ShadcnButton onClick={handleOpenDrawer} className="flex items-center gap-2" variant="outline">
      <PlusIcon />
      Connect Catalogs
    </ShadcnButton>
  );

  return (
    <>
      <SectionCard
        icon={<BoxCheckIcon />}
        subtitle="Products"
        title="Product Catalog"
        description="Add catalogs to your project to send your creators a better and defined order submitting experience."
        rightContent={connectCatalogButton}
      >
        {loading && <Skeleton />}
        {!loading && (selectedCatalogs.length ? (
          <CatalogsListV2 projectConfigId={projectConfig?.id} catalogs={selectedCatalogs} allCatalogStats={allCatalogStats} />
        ) : (
          <EmptyScreen />
        ))}
      </SectionCard>
      <ConnectCatalogDrawer programId={programId} />
    </>
  );
};

export const ProductSettings = ({ programId }: ProductCatalogsProps) => {
  const { apolloClient } = useAppContext();
  const { data: { project = undefined } = {} } = useProjectByIdQuery({
    variables: {
      id: programId,
    },
    skip: !programId,
    client: apolloClient,
  });
  const sections = [
    {
      id: 'product-catalog',
      label: 'Product Catalog',
      component: <ProductCatalogContent programId={programId} />,
    },
    {
      id: 'product-email-template',
      label: 'Product Email Template',
      component: <ApolloProvider client={apolloClient}><ProductEmailTemplate projectId={programId} program={project} /></ApolloProvider>,
    },
  ];

  return (
    <SidebarLayout
      sections={sections}
      activeSectionId="product-catalog"
      onlyShowActiveSection
    />
  );
};
